import React from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Home as HomeIcon,
  TravelExplore as GuidesIcon,
  MyLocation as TripsIcon,
  Person as ProfileIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NavBreadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['common', 'general']);

  // Define breadcrumb configurations for different routes
  const breadcrumbConfig = {
    '/': {
      label: t('navigation.home', { ns: 'common' }),
      icon: <HomeIcon sx={{ fontSize: 16 }} />,
    },
    '/guides': {
      label: t('navigation.guides', { ns: 'common' }),
      icon: <GuidesIcon sx={{ fontSize: 16 }} />,
      parent: '/',
    },
    '/my-trips': {
      label: t('header.nav.myTrips', { ns: 'common' }),
      icon: <TripsIcon sx={{ fontSize: 16 }} />,
      parent: '/',
    },
    '/profile': {
      label: t('navigation.profile', { ns: 'common' }),
      icon: <ProfileIcon sx={{ fontSize: 16 }} />,
      parent: '/',
    },
  };

  // Generate breadcrumb path
  const generateBreadcrumbs = () => {
    const currentPath = location.pathname;
    const current = breadcrumbConfig[currentPath];
    
    if (!current) return [];

    const breadcrumbs = [];
    let path = currentPath;
    
    // Build breadcrumb chain
    while (path && breadcrumbConfig[path]) {
      const config = breadcrumbConfig[path];
      breadcrumbs.unshift({
        path,
        label: config.label,
        icon: config.icon,
        isLast: path === currentPath,
      });
      path = config.parent;
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home page or if no config exists
  if (breadcrumbs.length <= 1) {
    return null;
  }

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  const handleBackClick = () => {
    // Navigate to the previous breadcrumb
    if (breadcrumbs.length > 1) {
      navigate(breadcrumbs[breadcrumbs.length - 2].path);
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
    }
  };

  // On mobile, show only last 2 levels (or back button + current)
  const displayBreadcrumbs = isMobile && breadcrumbs.length > 2
    ? [
        { ...breadcrumbs[0], truncated: true }, // Show "..." for home
        ...breadcrumbs.slice(-1) // Show only last item
      ]
    : breadcrumbs;

  // Mobile: Show back button instead of full breadcrumbs
  if (isMobile) {
    return (
      <Box
        sx={{
          py: 1,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleBackClick}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
          aria-label={t('buttons.back', { ns: 'common' })}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.8rem' }}
        >
          {breadcrumbs.length > 2 && (
            <>
              <MoreHorizIcon sx={{ fontSize: 14, verticalAlign: 'middle', mx: 0.5 }} />
              /
            </>
          )}
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{
            fontWeight: 600,
            fontSize: '0.9rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {breadcrumbs[breadcrumbs.length - 1].label}
        </Typography>
      </Box>
    );
  }

  // Desktop: Show full breadcrumbs
  return (
    <Box
      sx={{
        py: 1.5,
        px: 3,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-ol': {
            alignItems: 'center',
          },
        }}
      >
        {displayBreadcrumbs.map((crumb, index) => {
          const isLast = crumb.isLast;
          
          // Show truncation indicator
          if (crumb.truncated) {
            return (
              <Typography
                key="truncated"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                }}
              >
                <MoreHorizIcon fontSize="small" />
              </Typography>
            );
          }
          
          if (isLast) {
            return (
              <Typography
                key={crumb.path}
                color="text.primary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {crumb.icon}
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={crumb.path}
              color="inherit"
              onClick={() => handleBreadcrumbClick(crumb.path)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main',
                },
              }}
            >
              {crumb.icon}
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default NavBreadcrumbs;
